/* Respondent Card */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import geoJson from "../data/bd-geo-units.json";
import { PersonRecord } from "../types/data";
import { convertInCurrentLang, convertNumberInCurrentLang } from "../utils/misc";


const RespondentCard = ({
  person,
  onDelete,
  onClick
}: {
  person: PersonRecord;
  onDelete: (id: string) => void;
  onClick: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const lastLog = person.records?.[person.records.length - 1];

  const handleDelete = (id: string) => {
      onDelete(id);
  };

  const handleClick = (id: string) => {
    // window.alert(`Clicked on ${id}`);
    onClick(id);
  };

  const districts = geoJson.divisions.flatMap((div) => div.districts);

  const getDistrictName = (districtId: string): string => {
    const district = districts.find((d) => d.id === districtId);
    return district
      ? lang === "bn"
        ? district.name_bn
        : district.name_en
      : "";
  };

  const getUpazilaName = (upazilaId: string, districtId: string): string => {
    const district = districts.find((d) => d.id === districtId);
    const upazila = district?.upazilas.find((u) => u.id === upazilaId);
    return upazila ? (lang === "bn" ? upazila.name_bn : upazila.name_en) : "";
  };

  const getUnionName = (
    unionId: string,
    upazilaId: string,
    districtId: string
  ): string => {
    const district = districts.find((d) => d.id === districtId);
    const upazila = district?.upazilas.find((u) => u.id === upazilaId);
    const union = upazila?.unions.find((u) => u.id === unionId);
    return union ? (lang === "bn" ? union.name_bn : union.name_en) : "";
  };

  const formattedAddress = () => {
    if (!person.address) return "";
    const parts = [];
    if (person.address.village) parts.push(person.address.village);
    if (person.address.union) parts.push(getUnionName(person.address.union, person.address.upazila ?? "", person.address.district ?? ""));
    if (person.address.upazila) parts.push(getUpazilaName(person.address.upazila, person.address.district ?? ""));
    if (person.address.district) parts.push(getDistrictName(person.address.district));
    return parts.join(", ");
  }

  return (
    <div
      key={person.id}
      className="flex justify-between items-center p-4 rounded-2xl bg-slate-300 bg-opacity-20 backdrop-blur-md "
      onClick={() => handleClick(person.id)}
    >
      <div className="flex flex-col items-start flex-1 text-left justify-start space-y-1 overflow-hidden text-ellipsis">
        <p className="font-semibold text-2xl text-blue-700 text-ellipsis overflow-hidden">{person.name}</p>
        <div className="rounded-full flex items-center bg-slate-600 text-white border text-lg font-bold text-gray-700 px-3 min-h-8 shrink-0">
          {!person.sex && <span>{t("age")}:&nbsp;</span>} {convertNumberInCurrentLang(lang, person.age ? person.age : 0)}
          {person.sex && <span>, {t(person.sex ? person.sex : "other")}</span>}
        </div>
       
        {person.address && (
          <p className="text-lg font-semibold text-gray-600">
            {formattedAddress()}
          </p>
        )}

        <p className="text-lg text-blue-500 mt-1">
          <span className="font-bold">
            {convertNumberInCurrentLang(lang, person.records?.length || 0)}
          </span>
          {lang === "bn" ? " দিনের লগ করা হয়েছে" : " day(s) logged"}
        </p>
        {lastLog && (
          <p className="text-sm text-gray-500">
            {lang === "bn" ? "সর্বশেষ লগ:" : "Last log:"}{" "}
            {convertInCurrentLang(
              lang,
              new Date(lastLog.date).toLocaleDateString(lang === "bn" ? "bn-BD" : "en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            )}
          </p>
        )}
      </div>

      <div className="flex flex-col space-y-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/survey/new/${person.id}`);
          }}
          className="grow button-xs button-bg-grad-green"
        >
          <div className="flex flex-row gap-1 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {t("logDay")}
          </div>
        </button>

        {
          (person.records?.length || 0) > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/log-history/${person.id}?from_person=${person.id}`);
            }}
            className="grow button-xs button-bg-grad-2"
          >
            <div className="flex flex-row gap-1 items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
              </svg>
              {t("allLogs")}
            </div>
          </button>
        )}

        {/* <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(person.id);
          }}
          className="grow button-xs button-bg-grad-red"
        >
          <div className="flex flex-row gap-1 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>

            {t("delete")}
          </div>
        </button> */}
      </div>
    </div>
  );
};

export default RespondentCard;
